import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Loader2, MailCheck } from "lucide-react";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { signUp } from "../lib/authApi";
import { GoogleButton } from "../components/GoogleButton";

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

const TURNSTILE_SITE_KEY = "0x4AAAAAAEcon4hYVLML-4Mi";
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const widgetId = useRef(null);

  // Load Cloudflare Turnstile once and render the managed widget on signup only.
  useEffect(() => {
    if (sent) return undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !captchaRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(captchaRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => setCaptchaToken(""),
        "expired-callback": () => setCaptchaToken(""),
      });
    };

    if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      render();
      if (!window.turnstile) {
        const t = setInterval(() => {
          if (window.turnstile) {
            clearInterval(t);
            render();
          }
        }, 200);
        return () => {
          cancelled = true;
          clearInterval(t);
        };
      }
    }
    return () => {
      cancelled = true;
    };
  }, [sent]);

  const resetCaptcha = () => {
    setCaptchaToken("");
    try {
      if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
    } catch {
      /* ignore */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password || !confirm) {
      setError("Please fill in all fields.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!captchaToken) {
      setError("Please complete the verification below.");
      return;
    }
    setBusy(true);
    try {
      await signUp(email, password, captchaToken);
      setSent(true);
    } catch (err) {
      setError(err.message);
      resetCaptcha(); // Turnstile tokens are single-use — get a fresh one.
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="signup-confirm"
        className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#050505] px-8 text-center"
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#ff2a00]/15">
          <MailCheck className="h-8 w-8 text-[#ff2a00]" />
        </div>
        <h1 className="font-display mt-6 text-2xl font-extrabold tracking-tight text-white">
          Check your email
        </h1>
        <p className="mt-3 max-w-[300px] text-[15px] leading-relaxed text-white/60">
          We've sent a confirmation link to{" "}
          <span className="font-semibold text-white">{email}</span>. Tap it to
          activate your RoadRnr account, then come back and log in.
        </p>
        <div
          data-testid="signup-spam-notice"
          className="mt-6 w-full max-w-[320px] rounded-2xl border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-4 py-4 text-left"
        >
          <p className="text-sm font-extrabold uppercase tracking-wide text-[#ff2a00]">
            Check your junk or spam folder
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-white/70">
            The email can take a few minutes to arrive, and it often lands in
            junk/spam. Please check there before requesting another.
          </p>
        </div>
        <button
          data-testid="signup-confirm-login"
          onClick={() => navigate("/login")}
          className="mt-8 rounded-full bg-[#ff2a00] px-8 py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
        >
          Go to log in
        </button>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-sm font-medium text-white/40 transition-colors hover:text-white"
        >
          Keep browsing events
        </button>
      </div>
    );
  }

  return (
    <div data-testid="signup-page" className="min-h-[100dvh] bg-[#050505] px-6 pb-16 pt-6">
      <button
        data-testid="signup-back"
        onClick={() => navigate("/")}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0d0d0d] transition-transform duration-200 active:scale-90"
        aria-label="Back to browsing"
      >
        <ChevronLeft className="h-5 w-5 text-white" />
      </button>

      <div className="mx-auto mt-4 max-w-md">
        <Logo />
        <h1 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-white">
          Create account
        </h1>
        <p className="mt-1 text-sm text-white/50">
          It's free — browse without one anytime.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Email
            </Label>
            <Input
              data-testid="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={fieldClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Password
            </Label>
            <Input
              data-testid="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className={fieldClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Confirm password
            </Label>
            <Input
              data-testid="signup-confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className={fieldClass}
            />
          </div>

          {error && (
            <p data-testid="signup-error" className="text-sm text-[#ff6a4d]">
              {error}
            </p>
          )}

          <div data-testid="signup-turnstile" ref={captchaRef} className="flex justify-center" />

          <button
            type="submit"
            data-testid="signup-submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
          </button>
        </form>

        <GoogleButton testid="signup-google" />

        <p className="mt-6 text-center text-sm text-white/50">
          Already have an account?{" "}
          <Link
            data-testid="signup-to-login"
            to="/login"
            className="font-semibold text-[#ff2a00]"
          >
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
